import { useMemo } from 'react';

import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetRopaApi from 'config/api/assetRopa/assetRopa';
import invalidateOwnerAssetRopaQueries from 'config/api/assetRopa/assetRopaQuery.helpers';
import {
  buildOwnerWorkspaceFilterOptions,
  DEFAULT_OWNER_WORKSPACE_FILTERS,
  ownerWorkspaceFiltersToQueryParams,
  type OwnerWorkspaceFilters,
} from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import type { AddProcessingActivityInput, SoftDeleteProcessingActivityInput } from 'config/api/assetRopa/assetRopaWorkspace.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';

type UseOwnerAssetRopaWorkspaceOptions = {
  filters: OwnerWorkspaceFilters;
  organizationId?: number;
};

const OWNER_WORKSPACE_SESSION_FILTERS_KEY = 'OWNER_ASSET_ROPA_WORKSPACE_FILTERS';

/**
 * Owner workspace is scoped via asset-ropa/mine* only — never admin review assets list.
 */
const useOwnerAssetRopaWorkspace = ({ filters, organizationId }: UseOwnerAssetRopaWorkspaceOptions) => {
  const queryClient = useQueryClient();
  const filterParams = ownerWorkspaceFiltersToQueryParams(filters);

  const workspaceQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_WORKSPACE, organizationId, filterParams],
    async () => {
      const { data } = await assetRopaApi.getOwnerWorkspace(organizationId as number, filterParams);
      return data;
    },
    {
      enabled: Boolean(organizationId),
      retry: false,
      keepPreviousData: true,
    },
  );

  const metricsQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE_METRICS, organizationId],
    async () => {
      const { data } = await assetRopaApi.getMineMetrics(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId),
      retry: false,
      keepPreviousData: true,
    },
  );

  const workspaceData = workspaceQuery.data;
  const filteredAssets = workspaceData?.assets ?? [];
  const allAssets = filteredAssets;

  const filterOptions = useMemo(() => buildOwnerWorkspaceFilterOptions(allAssets), [allAssets]);

  const workspaceMetrics = metricsQuery.data?.workspaceMetrics ?? workspaceData?.workspaceMetrics;
  const reviewMetrics = metricsQuery.data !== undefined ? metricsQuery.data.reviewMetrics : workspaceData?.reviewMetrics ?? null;

  const invalidateWorkspace = async () => {
    await invalidateOwnerAssetRopaQueries(queryClient, organizationId);
  };

  const addProcessingActivityMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_ADD_PROCESSING_ACTIVITY,
    async ({ assetRopaId, payload }: { assetRopaId: number; payload: AddProcessingActivityInput }) => {
      const { data } = await assetRopaApi.addProcessingActivity(organizationId as number, assetRopaId, payload);
      return data;
    },
    { onSuccess: invalidateWorkspace },
  );

  const softDeleteMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_SOFT_DELETE_APA,
    async ({ apaId, assetRopaId, payload }: { apaId: number; assetRopaId: number; payload: SoftDeleteProcessingActivityInput }) => {
      const { data } = await assetRopaApi.softDeleteProcessingActivity(organizationId as number, assetRopaId, apaId, payload);
      return data;
    },
    { onSuccess: invalidateWorkspace },
  );

  const isForbidden = Boolean(
    workspaceQuery.isError && axios.isAxiosError(workspaceQuery.error) && workspaceQuery.error.response?.status === 403,
  );

  const { isLoading } = workspaceQuery;
  const isError = workspaceQuery.isError && !isForbidden;

  return {
    activeReview: workspaceData?.activeReview ?? null,
    addProcessingActivityMutation,
    allAssets,
    count: workspaceData?.count ?? filteredAssets.length,
    filteredAssets,
    filterOptions,
    invalidateWorkspace,
    isError,
    isForbidden,
    isLoading,
    refetch: async () => {
      await Promise.all([workspaceQuery.refetch(), metricsQuery.refetch()]);
    },
    reviewMetrics,
    softDeleteMutation,
    workspaceMetrics,
  };
};

export { DEFAULT_OWNER_WORKSPACE_FILTERS, OWNER_WORKSPACE_SESSION_FILTERS_KEY };
export type { OwnerWorkspaceFilters };
export default useOwnerAssetRopaWorkspace;
